import React, { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { useCardPayment } from '../lib/cardTerminal';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { CheckCircle, Money, CreditCard, ArrowClockwise, User, Coin } from '@phosphor-icons/react';

/**
 * BookingSharesPanel
 *
 * Shows the per-player share list for a booking and lets:
 *  - the customer (booker) pay their outstanding share online
 *  - admin/staff mark any share paid via cash/card (creates a Kasse entry)
 *  - admin/staff reverse a mistaken payment
 *
 * Props:
 *   - bookingId: string
 *   - onChange?: () => void   — parent refresh callback
 *   - compact?: boolean       — small inline mode for calendar tiles
 */
const BookingSharesPanel = ({ bookingId, onChange, compact = false }) => {
  const { API, user, language } = useAuth();
  const payByCard = useCardPayment();
  const isStaff = user?.role === 'admin' || user?.role === 'staff';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingShareId, setPayingShareId] = useState(null); // for online-pay spinner
  const [payDialogFor, setPayDialogFor] = useState(null);   // {share, method}
  const [playerName, setPlayerName] = useState('');
  const [processing, setProcessing] = useState(false);

  const L = (en, de) => (language === 'de' ? de : en);

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get(`/bookings/${bookingId}/shares`);
      setData(r.data);
    } catch (e) {
      // Not authorized or not found — silently hide
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  if (loading || !data) return null;
  const { shares = [], num_players, pay_scope, amount_gross, member_discount } = data;
  if (!shares.length || (num_players <= 1)) return null;

  const paidCount = shares.filter(s => s.is_paid).length;
  const totalPaid = shares.filter(s => s.is_paid).reduce((s, sh) => s + Number(sh.amount || 0), 0);

  // Customer-facing online pay for own unpaid share
  const handleOnlinePay = async (share) => {
    setPayingShareId(share.id);
    try {
      const r = await API.post('/payments/checkout', {
        booking_id: bookingId,
        share_id: share.id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Payment failed', 'Zahlung fehlgeschlagen'));
      setPayingShareId(null);
    }
  };

  const openPayDialog = (share, method) => {
    setPayDialogFor({ share, method });
    setPlayerName(share.name || '');
  };

  const confirmPay = async () => {
    if (!payDialogFor) return;
    setProcessing(true);
    try {
      if (payDialogFor.method === 'card') {
        const term = await payByCard(Number(payDialogFor.share.amount), `Platz ${playerName || ''}`.trim());
        if (!term.ok) {
          toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}`, { duration: 10000 });
          return;
        }
      }
      await API.put(`/bookings/${bookingId}/shares/${payDialogFor.share.id}/pay`, {
        payment_method: payDialogFor.method,
        player_name: playerName,
      });
      toast.success(L('Payment recorded', 'Zahlung erfasst'));
      setPayDialogFor(null);
      setPlayerName('');
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehler'));
    } finally {
      setProcessing(false);
    }
  };

  const handleUnpay = async (share) => {
    if (!window.confirm(L('Reverse this payment?', 'Diese Zahlung rückgängig machen?'))) return;
    try {
      await API.put(`/bookings/${bookingId}/shares/${share.id}/unpay`);
      toast.success(L('Payment reversed', 'Zahlung rückgängig gemacht'));
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehler'));
    }
  };

  // Compact badge (used in calendar tiles / booking list rows)
  if (compact) {
    const color = paidCount === shares.length ? 'bg-emerald-500' : paidCount > 0 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${color}`}
        title={L(`${paidCount} of ${shares.length} shares paid`, `${paidCount} von ${shares.length} Anteilen bezahlt`)}
        data-testid={`shares-badge-${bookingId}`}
      >
        {paidCount}/{shares.length}
      </span>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="booking-shares-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div>
          <div className="text-sm font-bold text-slate-900">
            {L('Payment Shares', 'Zahlungs-Anteile')} · {paidCount}/{shares.length} {L('paid', 'bezahlt')}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {L('Total', 'Total')}: {totalPaid.toFixed(2)} / {Number(amount_gross - (member_discount || 0)).toFixed(2)} CHF
            {member_discount > 0 && ` · ${L('Member discount', 'Member-Rabatt')}: −${Number(member_discount).toFixed(2)}`}
            {pay_scope && ` · ${pay_scope === 'self' ? L('Booker paid own share', 'Booker zahlt nur Anteil') : L('Booker paying whole court', 'Booker zahlt ganzen Platz')}`}
          </div>
        </div>
        <button onClick={load} className="p-1.5 hover:bg-slate-200 rounded-lg" title={L('Refresh', 'Aktualisieren')}>
          <ArrowClockwise size={14} className="text-slate-500" />
        </button>
      </div>

      {/* Share rows */}
      <ul className="divide-y divide-slate-100">
        {shares.map((s, i) => {
          const isMine = user && s.user_id === user.id;
          const label = s.name || (s.is_booker ? L('Booker', 'Booker') : L(`Player ${i + 1}`, `Spieler ${i + 1}`));
          return (
            <li key={s.id} className="px-4 py-4 flex items-center gap-4" data-testid={`share-row-${s.id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${s.is_paid ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                {s.is_paid
                  ? <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                  : <User size={18} className="text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {label}
                  {s.is_booker && <span className="text-[9px] uppercase font-bold text-slate-400">Booker</span>}
                  {s.is_member && <span className="text-[9px] uppercase font-bold text-blue-600">✓ Member</span>}
                </div>
                <div className="text-[11px] text-slate-500">
                  {Number(s.amount).toFixed(2)} CHF
                  {s.member_discount > 0 && ` · ${L('incl.', 'inkl.')} −${Number(s.member_discount).toFixed(2)} ${L('discount', 'Rabatt')}`}
                  {s.is_paid && ` · ${L('via', 'via')} ${s.payment_method || 'online'}`}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                {s.is_paid ? (
                  isStaff ? (
                    <button
                      onClick={() => handleUnpay(s)}
                      className="text-[11px] px-3 py-2 rounded-md text-red-600 hover:bg-red-50 font-semibold uppercase"
                      data-testid={`unpay-share-${s.id}`}
                    >
                      {L('Undo', 'Rückgängig')}
                    </button>
                  ) : (
                    <span className="text-[11px] text-emerald-700 font-bold uppercase">{L('Paid', 'Bezahlt')}</span>
                  )
                ) : (
                  <>
                    {/* Customer can pay their OWN share online */}
                    {isMine && !isStaff && (
                      <button
                        onClick={() => handleOnlinePay(s)}
                        disabled={payingShareId === s.id}
                        className="text-[11px] font-bold px-3 py-2 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                        data-testid={`pay-my-share-${s.id}`}
                      >
                        {payingShareId === s.id ? '…' : L('Pay online', 'Online zahlen')}
                      </button>
                    )}
                    {isStaff && (
                      <>
                        <button
                          onClick={() => openPayDialog(s, 'cash')}
                          className="min-w-[44px] h-10 px-2 rounded-md bg-slate-800 text-white hover:bg-slate-900 flex items-center justify-center"
                          data-testid={`staff-pay-cash-${s.id}`}
                          title={L('Cash', 'Bar')}
                        >
                          <Money size={18} weight="fill" />
                        </button>
                        <button
                          onClick={() => openPayDialog(s, 'card')}
                          className="min-w-[44px] h-10 px-2 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center"
                          data-testid={`staff-pay-card-${s.id}`}
                          title={L('Card', 'Karte')}
                        >
                          <CreditCard size={18} />
                        </button>
                        <button
                          onClick={() => openPayDialog(s, 'css_coins')}
                          className="min-w-[44px] h-10 px-2 rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-700 flex items-center justify-center"
                          data-testid={`staff-pay-css-${s.id}`}
                          title={L('CSS Coins — not counted in daily till (~2 weeks payout)', 'CSS Coins — nicht im Tages-Total (Auszahlung ~2 Wochen)')}
                        >
                          <Coin size={18} weight="fill" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Staff pay dialog — asks for optional player name */}
      <Dialog open={!!payDialogFor} onOpenChange={(o) => !o && setPayDialogFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {L('Collect payment', 'Zahlung kassieren')} · {payDialogFor?.method === 'cash' ? L('Cash', 'Bar') : L('Card', 'Karte')}
            </DialogTitle>
          </DialogHeader>
          {payDialogFor && (
            <div className="space-y-3 pt-1">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{L('Amount', 'Betrag')}</span>
                  <span className="font-bold">{Number(payDialogFor.share.amount).toFixed(2)} CHF</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">{L('Player name (optional)', 'Spieler-Name (optional)')}</label>
                <Input
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder={L('Alice', 'Alice')}
                  className="mt-1"
                  data-testid="staff-pay-player-name"
                />
              </div>
              <Button
                onClick={confirmPay}
                disabled={processing}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11"
                data-testid="staff-pay-confirm"
              >
                {processing ? '…' : L('Confirm & record', 'Bestätigen & erfassen')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingSharesPanel;
